import { Box, Flex, Text, Button, Card, RadioCards, IconButton, AlertDialog, Separator, TextArea } from '@radix-ui/themes';
import { DotFilledIcon } from '@radix-ui/react-icons';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { CheckIcon, Cross2Icon } from '@radix-ui/react-icons';
import { DateTime } from 'luxon';
import LoadingIndicator from '../shared/LoadingIndicator';
import Carousel from '../../components/carousel/Carousel'
import { getQuiz, saveQuizAttempt } from '../../api/quiz';
import { toast } from 'react-toastify';
import UserAvatar from '../common/UserAvatar';
import Timer from '../common/Timer';

const ShortAnswerInput = ({ onSubmit, placeholder = "Enter your answer...", defaultValue }) => {
    const [answer, setAnswer] = useState(defaultValue || '');

    const handleSubmit = (e) => {
        e.preventDefault();
        if (answer.trim()) {
            onSubmit(answer);
            setAnswer(''); // Clear the input after submission
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="2">
                <TextArea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={placeholder}
                    style={{ minHeight: '100px' }}
                />
                <Button type="submit" variant="solid" disabled={!answer.trim()}>
                    Submit Answer
                </Button>
            </Flex>
        </form>
    );
};

const StudentQuiz = ({ summary, onComplete, setSelectedQuiz }) => {
    const subscriptions = useSelector((state) => state.studentAccount.subscriptions);
    console.log('subscriptions', subscriptions);
    const account = useSelector((state) => state.studentAccount);
    const [loading, setLoading] = useState(true);
    const [quiz, setQuiz] = useState(null);
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [course, setCourse] = useState(null);
    const [slideIndex, setSlideIndex] = useState(0);
    const [questionStartTime, setQuestionStartTime] = useState(null);
    const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

    const [quizAttempt, setQuizAttempt] = useState({
        quiz_id: summary.id,
        student_id: account.details.id,
        started_at: null,
        completed_at: null,
        question_attempts: []
    });

    const fetchQuizData = async () => {
        setLoading(true);
        try {
            const response = await getQuiz(summary.id);
            setQuiz(response);
        } catch (error) {
            console.error('Error fetching quiz:', error);
            toast.error('Failed to load quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        setCourse(subscriptions?.find(sub => sub.course_id == summary.course_id)?.course);
        fetchQuizData();
    }, []);

    useEffect(() => {
        if (quiz?.questions?.length > 0) {
            setQuizAttempt(prev => ({
                ...prev,
                question_attempts: quiz.questions.map(question => ({
                    question_id: question.id,
                    answer: null,
                    time_taken: null
                }))
            }));
        }
    }, [quiz]);

    useEffect(() => {
        if (slideIndex > 0 && slideIndex <= quiz?.questions?.length) {
            setQuestionStartTime(DateTime.now());
        }
    }, [slideIndex]);

    const handleStartQuiz = async () => {
        setQuizAttempt(prev => ({
            ...prev,
            started_at: DateTime.now().toFormat("yyyy-MM-dd HH:mm:ss")
        }));
        setSlideIndex(1);
        setQuestionStartTime(DateTime.now());
    }

    const handleCompleteQuiz = async () => {
        onComplete();
    }

    const handleNext = () => {
        // if (slideIndex === quiz.questions.length) {
        //     console.log('submitting');
        //     handleSubmitQuiz({
        //         ...quizAttempt,
        //     });
        // }
        // console.log('slideIndex', slideIndex);
        setSlideIndex(slideIndex + 1);
    }

    const handleSubmitQuiz = async (attemptData) => {
        setLoading(true);
        try {
            const unansweredQuestions = attemptData.question_attempts.filter(
                attempt => attempt.answer === null
            );

            if (unansweredQuestions.length > 0) {
                toast.error('Please answer all questions before submitting');
                setLoading(false);
                return;
            }

            const response = await saveQuizAttempt(attemptData);
            toast.success('Quiz submitted successfully. You will be notified when your results are ready.');
            onComplete(response.quiz_attempt);
        } catch (error) {
            console.error('Error saving quiz:', error);
            toast.error('Failed to submit quiz. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    const getStudentAnswer = (questionId) => {
        const answer = quizAttempt.question_attempts.find(attempt => attempt.question_id == questionId);
        if (answer?.answer) {
            return (
                <Flex direction="column" gap="2">
                    <Text>{answer.answer}</Text>
                    <Text size="1" color="gray">Your answer has been submitted and will be graded.</Text>
                </Flex>
            );
        }
        return <Text>You have not answered this question</Text>;
    }

    const answerQuestion = (questionId, answer) => {
        const question = quiz.questions.find(question => question.id == questionId);
        let answerText;

        if (question.type === 'multiple_choice') {
            const selectedAnswer = question.answers.find(ans => ans.id == answer);
            answerText = selectedAnswer.text;
        } else {
            answerText = answer;
        }

        const endTime = DateTime.now();
        const timeTaken = questionStartTime ?
            Math.round(endTime.diff(questionStartTime, 'seconds').seconds) :
            null;

        setQuizAttempt(prev => ({
            ...prev,
            completed_at: DateTime.now().toFormat("yyyy-MM-dd HH:mm:ss"),
            question_attempts: prev.question_attempts.map(attempt =>
                attempt.question_id === questionId
                    ? {
                        ...attempt,
                        answer: answerText,
                        time_taken: timeTaken
                    }
                    : attempt
            )
        }));
        setQuestionStartTime(null);
    };

    // useEffect(() => {
    //     console.log('quizAttempt', quizAttempt);
    //     if (quizAttempt?.question_attempts?.length === quiz?.questions?.length &&
    //         quizAttempt.question_attempts.every(attempt => attempt.answer !== null)) {
    //         handleSubmitQuiz(quizAttempt);
    //     }
    // }, [quizAttempt]);

    const renderQuestion = (question, index) => {
        const hasAnswer = quizAttempt.question_attempts.find(
            attempt => attempt.question_id == question.id
        )?.answer;

        return (
            <Box key={question.id} p="4">
                <Text size="3" weight="bold" mb="2">
                    Question {index + 1}
                </Text>
                <Text as="p" mb="4">
                    {question.text}
                </Text>
                <Flex direction="column" gap="2">
                    {question.type === 'multiple_choice' ? (
                        <RadioCards.Root
                            onValueChange={(value) => {
                                answerQuestion(question.id, value);
                                handleNext();
                            }}
                            defaultValue={hasAnswer ? question.answers.find(ans => ans.text === hasAnswer)?.id : null}
                        >
                            {question.answers.map((answer, optIndex) => (
                                <RadioCards.Item value={answer.id} key={optIndex} width="100%">
                                    {answer.text}
                                </RadioCards.Item>
                            ))}
                        </RadioCards.Root>
                    ) : (
                        <ShortAnswerInput
                            onSubmit={(answer) => {
                                answerQuestion(question.id, answer);
                                handleNext();
                            }}
                            placeholder="Type your answer here..."
                            defaultValue={hasAnswer}
                        />
                    )}
                </Flex>
            </Box>
        );
    };

    const getAttemptedCount = () => {
        return quizAttempt.question_attempts.filter(attempt => attempt.answer !== null).length;
    };

    if (loading) {
        return <LoadingIndicator />
    }

    return (
        <Box>

            <Carousel
                currentIndex={slideIndex}
                onIndexChange={setSlideIndex}
                disableControls={true}
            >
                <Flex direction="column" gap="4" align="center" justify="center" width="80%" maxWidth="400px">
                    <Box width="100%">
                        <Text size="2" as="p" mb="2" weight="bold" align="left">
                            {summary.lesson.title}
                        </Text>

                        <Flex justify="between" align="center" width="100%" mb="2">
                            <Flex align="start" gap="1">
                                <Text size="1" color="gray">
                                    {summary.teaching_plan.course.subject.name}
                                </Text>
                                <DotFilledIcon style={{ color: 'var(--gray-9)' }} />
                                <Text size="1" color="gray">
                                    {summary.type.replace('_', ' ')}
                                </Text>
                            </Flex>
                            <Text size="1" color="gray">
                                {DateTime.fromISO(summary.created_at).toRelative()}
                            </Text>
                        </Flex>
                        <Separator size="4" />
                    </Box>

                    {summary.description && (
                        <Box my="6" width="100%">
                            <Text size="2" as="p" align="left" style={{ color: 'var(--gray-12)' }}>{summary.description}</Text>
                            <Flex justify="end" gap="2" align="center" width="100%">
                                <Text size="1" color="gray">{summary.teaching_plan.teacher.user.firstname} {summary.teaching_plan.teacher.user.lastname}</Text>
                                <UserAvatar user={summary.teaching_plan.teacher.user} radius='full' />
                            </Flex>
                        </Box>
                    )}

                    <Text size="1" mb="4" as="p" color="gray">
                        NB: This quiz is timed. You must complete every question. Exiting the quiz will submit your answers as an attempt.
                    </Text>



                    <div className="flex flex-row gap-2 justify-center items-center">
                        <Button onClick={handleStartQuiz} variant="solid" className='cursor-pointer'>
                            Start Quiz
                        </Button>

                        <Button onClick={() => setIsCloseModalOpen(true)} variant="solid" color='crimson' className='cursor-pointer'>
                            Cancel
                        </Button>
                    </div>

                    <Text size="2" as="p" color="gray" mb="4">
                        Attempts: {summary.quiz_attempts?.length || 0} / {summary.attempts}
                    </Text>
                </Flex>


                {quiz.questions.map((question, index) => renderQuestion(question, index))}

                <Flex direction="column" justify="center" align="center" p="4" gap="4">
                    <Text as="p" size="3" weight="bold">
                        Quiz Summary
                    </Text>
                    <Text as="p" size="2">
                        You have answered {getAttemptedCount()} out of {quiz.questions.length} questions
                    </Text>
                    <Button
                        onClick={() => handleSubmitQuiz(quizAttempt)}
                        variant="solid"
                        disabled={getAttemptedCount() !== quiz.questions.length}
                    >
                        Submit Quiz
                    </Button>
                </Flex>
            </Carousel>

            {/* Navigation Bar */}
            {slideIndex > 0 && (
                <Flex
                    direction="column"
                    gap="2"
                    justify="center"
                    align="center"
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        padding: '1rem',
                        background: 'var(--gray-4)',
                        borderTop: '1px solid var(--gray-5)',
                        zIndex: 100
                    }}
                >
                    <Flex gap="2" justify="center" align="center" width="100%" style={{ flexWrap: 'wrap' }}>
                        {quiz.questions.map((_, index) => {
                            const questionIndex = index + 1;
                            const hasAnswer = quizAttempt.question_attempts.find(
                                attempt => attempt.question_id === quiz.questions[index].id
                            )?.answer;

                            return (
                                <Button
                                    key={index}
                                    size="1"
                                    variant={slideIndex === questionIndex ? "solid" : "soft"}
                                    color={hasAnswer ? "green" : "gray"}
                                    onClick={() => setSlideIndex(questionIndex)}
                                    style={{
                                        minWidth: '2rem',
                                        height: '2rem',
                                        borderRadius: '50%',
                                        padding: 0
                                    }}
                                >
                                    {questionIndex}
                                </Button>
                            );
                        })}
                    </Flex>
                    <Button
                        variant="solid"
                        onClick={() => setSlideIndex(quiz.questions.length + 1)}
                        disabled={getAttemptedCount() !== quiz.questions.length}
                    >
                        Submit Quiz
                    </Button>
                </Flex>
            )}

            <AlertDialog.Root open={isCloseModalOpen} onOpenChange={setIsCloseModalOpen}>
                <AlertDialog.Content className="p-4 rounded shadow-lg w-1/3 mx-auto">
                    <AlertDialog.Title>Close quiz</AlertDialog.Title>
                    {/* <AlertDialog.Description>
                        If you do not complete and submit all the questions, the quiz will not be recorded. Are you sure you want to close?
                    </AlertDialog.Description> */}

                    <AlertDialog.Description>
                        Are you sure you want to close the quiz?
                    </AlertDialog.Description>

                    <div className="flex justify-end gap-4 mt-4">
                        <Button
                            className='cursor-pointer'
                            variant="soft"
                            onClick={() => {
                                setIsCloseModalOpen(false);
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            className='cursor-pointer'
                            variant="solid"
                            color="green"
                            onClick={() => setSelectedQuiz(null)}
                        >
                            Close
                        </Button>
                    </div>
                </AlertDialog.Content>
            </AlertDialog.Root>
        </Box>
    );
};

export default StudentQuiz;
